import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../hooks/usePageMeta';
import { ArrowRight, Calendar, Clock, User, Search, ChevronRight, TrendingUp } from 'lucide-react';
import { blogPosts, blogCategories, featuredPosts } from '../data/blogData';

export const Insights = ({ onOpenAuditModal }) => {
  usePageMeta();
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(12);

  const filteredPosts = useMemo(() => {
    return blogPosts.filter(post => {
      const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
      const matchesSearch = searchQuery === '' || 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.keywords.some(k => k.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const visiblePosts = filteredPosts.slice(0, visibleCount);
  const hasMore = visibleCount < filteredPosts.length;

  const loadMore = () => {
    setVisibleCount(prev => prev + 12);
  };

  return (
    <main className="pt-20 bg-[#F9FAFB]" data-testid="insights-page">
      {/* Hero Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm font-semibold tracking-wide uppercase text-[#FFAA17] mb-3">
              Insights & Resources
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#1A1A1A] mb-4">
              Marketing Knowledge Hub for Founders
            </h1>
            <p className="text-base md:text-lg text-[#525252]">
              100+ expert articles on AI, SEO, growth hacking, and digital marketing strategies for 2026. 
              Stay ahead of the competition with actionable insights.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      <section className="py-12 bg-[#F2F2F2]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center gap-2 mb-8">
            <TrendingUp className="w-5 h-5 text-[#FFAA17]" />
            <h2 className="text-xl font-bold text-[#1A1A1A]">Trending Articles</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredPosts.slice(0, 4).map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group"
                data-testid={`featured-post-${post.id}`}
              >
                <div className="bg-white rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300">
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-[#FFAA17] text-white text-xs font-semibold px-2 py-1 rounded-full">
                        Featured
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <span className="text-xs text-[#2F80ED] font-medium">{post.category}</span>
                    <h3 className="text-sm font-semibold text-[#1A1A1A] mt-1 group-hover:text-[#FFAA17] transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-3 text-xs text-[#525252]">
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-16 md:top-20 z-30">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {/* Search */}
          <div className="max-w-md mx-auto mb-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search 100+ articles..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setVisibleCount(12);
                }}
                className="w-full pl-12 pr-4 py-3 bg-[#F9FAFB] rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2F80ED]/30 focus:border-[#2F80ED] transition-all"
                data-testid="search-input"
              />
            </div>
          </div>

          {/* Categories - Horizontal Scroll */}
          <div className="overflow-x-auto pb-2 -mx-4 px-4">
            <div className="flex gap-2 min-w-max">
              <button
                onClick={() => {
                  setActiveCategory('All');
                  setVisibleCount(12);
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  activeCategory === 'All'
                    ? 'bg-[#FFAA17] text-white'
                    : 'bg-[#F2F2F2] text-[#525252] hover:bg-gray-200'
                }`}
                data-testid="category-all"
              >
                All ({blogPosts.length})
              </button>
              {blogCategories.map((category) => {
                const count = blogPosts.filter(p => p.category === category).length;
                return (
                  <button
                    key={category}
                    onClick={() => {
                      setActiveCategory(category);
                      setVisibleCount(12);
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                      activeCategory === category
                        ? 'bg-[#FFAA17] text-white'
                        : 'bg-[#F2F2F2] text-[#525252] hover:bg-gray-200'
                    }`}
                    data-testid={`category-${category}`}
                  >
                    {category} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-12 md:py-16 bg-[#F9FAFB]" data-testid="blog-posts-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {/* Results Count */}
          <p className="text-sm text-[#525252] mb-6">
            Showing {visiblePosts.length} of {filteredPosts.length} articles
            {activeCategory !== 'All' && ` in ${activeCategory}`}
            {searchQuery && ` matching "${searchQuery}"`}
          </p>

          {filteredPosts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {visiblePosts.map((post) => (
                  <article
                    key={post.id}
                    className="group"
                    data-testid={`blog-post-${post.id}`}
                  >
                    <Link to={`/blog/${post.slug}`}>
                      <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300">
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                          <div className="absolute top-4 left-4">
                            <span className="bg-[#FFAA17] text-white text-xs font-semibold px-3 py-1 rounded-full">
                              {post.category}
                            </span>
                          </div>
                        </div>
                        <div className="p-6">
                          <div className="flex items-center gap-4 text-xs text-[#525252] mb-3">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {post.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {post.readTime}
                            </span>
                          </div>
                          <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#FFAA17] transition-colors line-clamp-2">
                            {post.title}
                          </h3>
                          <p className="text-[#525252] text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                          <div className="flex items-center justify-between">
                            <span className="flex items-center gap-2 text-xs text-[#525252]">
                              <User className="w-3 h-3" />
                              {post.author}
                            </span>
                            <span className="text-[#FFAA17] font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                              Read <ChevronRight className="w-4 h-4" />
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="text-center mt-12">
                  <Button
                    onClick={loadMore}
                    variant="outline"
                    className="border-[#2F80ED] text-[#2F80ED] hover:bg-[#2F80ED]/5 rounded-full px-8"
                    data-testid="load-more-btn"
                  >
                    Load More Articles ({filteredPosts.length - visibleCount} remaining)
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-[#525252] mb-4">No articles found matching your criteria.</p>
              <Button
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategory('All');
                }}
                variant="outline"
                className="border-[#FFAA17] text-[#FFAA17] rounded-full"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="bg-[#2E2E2E] rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Get Weekly Marketing Insights
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Join 5,000+ founders and marketers who get our weekly insights on 
              digital marketing strategies, AI tools, and growth tactics.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FFAA17]"
                data-testid="newsletter-email"
              />
              <Button
                className="bg-[#FFAA17] hover:bg-[#E69500] text-white rounded-full px-6"
                data-testid="newsletter-submit"
              >
                Subscribe
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-[#F2F2F2]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4">
            Ready to Apply These Strategies?
          </h2>
          <p className="text-[#525252] mb-8 max-w-2xl mx-auto">
            Let us help you implement these strategies for your business. 
            Get a free growth audit to identify your biggest opportunities.
          </p>
          <Button
            onClick={onOpenAuditModal}
            className="bg-[#FFAA17] hover:bg-[#E69500] text-white rounded-full px-8 py-3"
            data-testid="insights-cta"
          >
            Get Free Growth Audit
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* CollectionPage Schema for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Marketing Insights & Resources - Digital Startup",
            "description": "100+ expert articles on AI, SEO, growth hacking, and digital marketing strategies for founders and business owners.",
            "url": "https://digitalstartup.in/insights",
            "numberOfItems": blogPosts.length,
            "publisher": {
              "@type": "Organization",
              "name": "Digital Startup"
            }
          })
        }}
      />
    </main>
  );
};

export default Insights;
